import { useMemo } from 'react';

import { ProductType } from '@common/enums';
import { getEEProductType } from '@common/helpers';
import { ProductDetails } from '@common/models';
import { convertProductsToProductDetailsList } from '@common/utils';
import useColors from '@ee-api/colors-api/useColors';
import { useCornerRadii } from '@ee-api/corner-radii-api/hooks/useCornerRadii';
import { useCutoutLaborProducts } from '@ee-api/cutout-labor-api/hooks/useCutoutLaborProducts';
import { useDiagonalCorners } from '@ee-api/diagonal-corners-api/useDiagonalCorners';
import { useEdges } from '@ee-api/edges-api/useEdges';
import { useEndCaps } from '@ee-api/endcaps-api/useEndCaps';
import { useEndSplash } from '@ee-api/endsplash-api/useEndSplash';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import { usePlumbing } from '@ee-api/plumbing-api/usePlumbing';
import { useStores } from '@ee-api/stores-api/useStores';
import { useWaterfallLabor } from '@ee-api/waterfall-labor-api/useWaterfallLabor';
import { convertWaterfallLaborToProductDetails } from '@ee-api/waterfall-labor-api/waterfallLabor.utils';

import useCookingAppliance from '../components/scrollable-area/CookingAppliancesSection/hooks/useCookingAppliance';
import { convertCutoutsToProductDetails } from '../components/scrollable-area/CutoutsSection/cutouts.utils';
import { convertColorsToProductDetails } from '../components/scrollable-area/colors/colors.utils';
import { convertCornerRadiiToProductDetails } from '../components/scrollable-area/corner-radius/corner-radius.utils';
import { convertDiagonalCornersToProductDetails } from '../components/scrollable-area/diagonal-corners/diagonal-corners.utils';
import useDishwasher from '../components/scrollable-area/dishwashers/useDishwasher';
import { convertEdgesToProductDetails } from '../components/scrollable-area/edges/edges.utils';
import useFaucets from '../components/scrollable-area/faucets/useFaucets';
import { convertRefrigeratorsToProductDetails } from '../components/scrollable-area/refrigerators/refrigerator.utils';
import useRefrigerator from '../components/scrollable-area/refrigerators/useRefrigerator';
import {
    convertSinkLaborToProductDetails,
    convertSinksToProductDetails,
} from '../components/scrollable-area/sinks/sinks.utils';
import useSink from '../components/scrollable-area/sinks/useSink';
import { convertTearOutsToProductDetails } from '../components/scrollable-area/tear-out-countertop/tear-outs.utils';

type UseProductsAsProductDetailsReturn = {
    cutoutsAsProductDetails: ProductDetails[];
    tearoutsAsProductDetails: ProductDetails[];
    chipMinimizerAsProductDetails: ProductDetails[];
    waterfallLaborAsProductDetails: ProductDetails[];
    cookingAppliancesAsProductDetails: ProductDetails[];
    faucetsAsProductDetails: ProductDetails[];
    optionalProductsAsProductDetails: ProductDetails[];
    cutoutLaborAsProductDetails: ProductDetails[];
    diagonalCornersAsProductDetails: ProductDetails[];
    sinksAsProductDetails: ProductDetails[];
    customerSuppliedSinksAsProductDetails: ProductDetails[];
    colorsAsProductDetails: ProductDetails[];
    cornerRadiiAsProductDetails: ProductDetails[];
    edgesAsProductDetails: ProductDetails[];
    dishwashersAsProductDetails: ProductDetails[];
    refrigeratorsAsProductDetails: ProductDetails[];
    plumbingAsProductDetails: ProductDetails[];
    endSplashesAsProductDetails: ProductDetails[];
    endCapsAsProductDetails: ProductDetails[];
    isFetchingAnything: boolean;
};

const emptyArray = [] satisfies ProductDetails[];

// TODO these are not in a consistent location
const convertFaucetsToProductDetails = convertProductsToProductDetailsList(ProductType.FAUCET);
const convertOptionalProductsToProductDetails = convertProductsToProductDetailsList(getEEProductType);
const convertCutoutLaborToProductDetails = convertProductsToProductDetailsList(ProductType.SINK_LABOR);
const convertDishwashersToProductDetails = convertProductsToProductDetailsList(ProductType.DISHWASHER);
const convertChipMinimizerToProductDetails = convertProductsToProductDetailsList(ProductType.LABOR);
const convertCookingAppliancesToProductDetails = convertProductsToProductDetailsList(ProductType.COOKING_APPLIANCE);
const convertPlumbingToProductDetails = convertProductsToProductDetailsList(ProductType.PLUMBING);

export const useProductsAsProductDetails = (): UseProductsAsProductDetailsReturn => {
    const { chipMinimizer, cutouts, tearOuts, optionalProducts, isFetchingOptionalItems } = useOptionalItems();
    const { waterfallLaborFee, isFetchingWaterfallLaborFee } = useWaterfallLabor();
    const { browsedCookingAppliances, isFetchingCookingAppliances, customerSuppliedCookingAppliances } =
        useCookingAppliance();
    const { browsedFaucets, isFetchingFaucets } = useFaucets();
    const { data: cutoutLabor, isFetching: isFetchingCutoutLabor } = useCutoutLaborProducts();
    const { diagonalCorners, isFetchingDiagonalCorners } = useDiagonalCorners();
    const { browsedSinks, customerSuppliedSinks, isFetchingSinks } = useSink();
    const { colors, isFetchingColors } = useColors();
    const { isFetchingMaterials } = useMaterials();
    const { isFetchingStores } = useStores();
    const { cornerRadii, isFetchingCornerRadii } = useCornerRadii();
    const { sortedEdges: edges, isFetching: isFetchingEdges } = useEdges();
    const { browsedDishwashers, isFetchingDishwashers } = useDishwasher();
    const { browsedRefrigerators, isFetchingRefrigerators } = useRefrigerator();
    const { plumbingItems, isFetchingPlumbing } = usePlumbing();
    const { data: endCaps, isFetchingEndCap } = useEndCaps();
    const { data: endSplashes, isFetchingEndSplash } = useEndSplash();

    const isFetchingAnything = useMemo(() => {
        const allValues = {
            isFetchingColors,
            isFetchingCookingAppliances,
            isFetchingCornerRadii,
            isFetchingCutoutLabor,
            isFetchingDiagonalCorners,
            isFetchingDishwashers,
            isFetchingEdges,
            isFetchingEndCap,
            isFetchingEndSplash,
            isFetchingFaucets,
            isFetchingMaterials,
            isFetchingOptionalItems,
            isFetchingPlumbing,
            isFetchingRefrigerators,
            isFetchingSinks,
            isFetchingStores,
            isFetchingWaterfallLaborFee,
        };
        const trueValues = Object.entries(allValues).filter(([_k, v]) => v);
        // useful to see how requests get processed
        // console.log('==fetching', trueValues.map(([k, _v]) => k).join(', '));
        return !!trueValues.length;
    }, [
        isFetchingColors,
        isFetchingCookingAppliances,
        isFetchingCornerRadii,
        isFetchingCutoutLabor,
        isFetchingDiagonalCorners,
        isFetchingDishwashers,
        isFetchingEdges,
        isFetchingEndCap,
        isFetchingEndSplash,
        isFetchingFaucets,
        isFetchingMaterials,
        isFetchingOptionalItems,
        isFetchingPlumbing,
        isFetchingRefrigerators,
        isFetchingSinks,
        isFetchingStores,
        isFetchingWaterfallLaborFee,
    ]);

    // TODO remove the logit function completely
    // const _mountTime = useRef(Date.now());
    const logit = (_data: string) => {
        return; //disable logging
        //console.log('==productDetails Update', (Date.now() - _mountTime.current) / 1000, ...data);
    };

    const endCapsAsProductDetails = useMemo(() => {
        logit('endCapsAsProductDetails');
        return convertProductsToProductDetailsList(ProductType.END_CAP)(endCaps);
    }, [endCaps]);

    const endSplashesAsProductDetails = useMemo(() => {
        logit('endSplashesAsProductDetails');
        return convertProductsToProductDetailsList(ProductType.END_SPLASH)(endSplashes);
    }, [endSplashes]);

    const cutoutsAsProductDetails = useMemo(() => {
        logit('cutoutsAsProductDetails');
        return convertCutoutsToProductDetails(cutouts);
    }, [cutouts]);

    const tearoutsAsProductDetails = useMemo(() => {
        logit('tearoutsAsProductDetails');
        return convertTearOutsToProductDetails(tearOuts);
    }, [tearOuts]);

    const chipMinimizerAsProductDetails = useMemo(() => {
        logit('chipMinimizerAsProductDetails');
        return chipMinimizer ? convertChipMinimizerToProductDetails([chipMinimizer]) : emptyArray;
    }, [chipMinimizer]);

    const waterfallLaborAsProductDetails = useMemo(() => {
        logit('waterfallLaborAsProductDetails');
        return waterfallLaborFee ? convertWaterfallLaborToProductDetails([waterfallLaborFee]) : emptyArray;
    }, [waterfallLaborFee]);

    const cookingAppliancesAsProductDetails = useMemo(() => {
        logit('cookingAppliancesAsProductDetails');
        const browsed = convertCookingAppliancesToProductDetails(browsedCookingAppliances);
        const userSupplied = convertCookingAppliancesToProductDetails(customerSuppliedCookingAppliances);
        return [...browsed, ...userSupplied];
    }, [browsedCookingAppliances, customerSuppliedCookingAppliances]);

    const faucetsAsProductDetails = useMemo(() => {
        logit('faucetsAsProductDetails');
        return convertFaucetsToProductDetails(browsedFaucets);
    }, [browsedFaucets]);

    const optionalProductsAsProductDetails = useMemo(() => {
        logit('optionalProductsAsProductDetails');
        return convertOptionalProductsToProductDetails(optionalProducts);
    }, [optionalProducts]);

    const cutoutLaborAsProductDetails = useMemo(() => {
        logit('cutoutLaborAsProductDetails');
        return convertCutoutLaborToProductDetails(cutoutLabor);
    }, [cutoutLabor]);

    const diagonalCornersAsProductDetails = useMemo(() => {
        logit('diagonalCornersAsProductDetails');
        return convertDiagonalCornersToProductDetails(diagonalCorners);
    }, [diagonalCorners]);

    const sinksAsProductDetails = useMemo(() => {
        logit('sinksAsProductDetails');
        return convertSinksToProductDetails(browsedSinks);
    }, [browsedSinks]);

    const customerSuppliedSinksAsProductDetails = useMemo(() => {
        logit('customerSuppliedSinksAsProductDetails');
        return convertSinkLaborToProductDetails(customerSuppliedSinks);
    }, [customerSuppliedSinks]);

    const colorsAsProductDetails = useMemo(() => {
        logit('colorsAsProductDetails');
        return convertColorsToProductDetails(colors);
    }, [colors]);

    const cornerRadiiAsProductDetails = useMemo(() => {
        logit('cornerRadiiAsProductDetails');
        return convertCornerRadiiToProductDetails(cornerRadii);
    }, [cornerRadii]);

    const edgesAsProductDetails = useMemo(() => {
        logit('edgesAsProductDetails');
        return convertEdgesToProductDetails(edges);
    }, [edges]);

    const dishwashersAsProductDetails = useMemo(() => {
        logit('dishwashersAsProductDetails');
        return convertDishwashersToProductDetails(browsedDishwashers);
    }, [browsedDishwashers]);

    const refrigeratorsAsProductDetails = useMemo(() => {
        logit('refrigeratorsAsProductDetails');
        return convertRefrigeratorsToProductDetails(browsedRefrigerators);
    }, [browsedRefrigerators]);

    const plumbingAsProductDetails = useMemo(() => {
        logit('plumbingAsProductDetails');
        return convertPlumbingToProductDetails(plumbingItems);
    }, [plumbingItems]);

    return {
        cutoutsAsProductDetails,
        tearoutsAsProductDetails,
        chipMinimizerAsProductDetails,
        waterfallLaborAsProductDetails,
        cookingAppliancesAsProductDetails,
        faucetsAsProductDetails,
        optionalProductsAsProductDetails,
        cutoutLaborAsProductDetails,
        diagonalCornersAsProductDetails,
        sinksAsProductDetails,
        customerSuppliedSinksAsProductDetails,
        colorsAsProductDetails,
        cornerRadiiAsProductDetails,
        edgesAsProductDetails,
        dishwashersAsProductDetails,
        refrigeratorsAsProductDetails,
        plumbingAsProductDetails,
        endCapsAsProductDetails,
        endSplashesAsProductDetails,
        isFetchingAnything,
    };
};
